<!--components\Inputs\InputCarPlate.vue-->
<template>
  <div class="license-plate">
    <div class="plate-frame" :class="plateFrameClass">
      <div class="inputs-container">
        <template v-for="(value, index) in inputs" :key="index">
          <div class="input-wrapper">
            <input
              v-model="inputs[index]"
              maxlength="1"
              :inputmode="getInputMode(index)"
              autocomplete="off"
              @keyup="handleKeyup($event, index)"
              @input="handleInput($event, index)"
              @click="handleCLick(index)"
              @focus="handleFocus(index)"
              ref="inputRefs"
              class="input-box w-full h-12 text-center text-lg rounded-md font-bold"
              :id="index === 0 ? 'first-input' : null"
            />
            <div class="input-line"></div>
          </div>
          <span v-if="index === 1" class="dot-separator h-12">•</span>
          <img v-if="index === 3" class="w-4 aspect-square" src="/img/shared/patente_chile.svg" alt="" />
        </template>
      </div>
      <span class="plate-text">CHILE</span>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, computed, watch } from "vue";

// Props para recibir el valor del store
const props = defineProps({
  value: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['changePlate', 'plateCompleted']);

const inputs = ref(["", "", "", "", "", ""]);
const inputRefs = ref([]);
const activeIndex = ref(0);
// Nuevo estado para trackear el modo de entrada preferido para posiciones 2 y 3
const preferredInputMode = ref(null);

const isPlateComplete = computed(() => {
  return inputs.value.every(input => input.trim() !== "");
});

const plateFrameClass = computed(() => {
  return {
    'plate-complete': isPlateComplete.value,
    'plate-incomplete': !isPlateComplete.value
  };
});

// Función para determinar el inputmode inteligente
const getInputMode = (index) => {
  // Posiciones 0-1: siempre texto (solo letras)
  if (index <= 1) {
    return 'text';
  }
  
  // Posiciones 4-5: siempre numérico
  if (index >= 4) {
    return 'numeric';
  }
  
  // Posiciones 2-3: lógica inteligente
  if (index === 2 || index === 3) {
    // Si ya tenemos un modo preferido, lo mantenemos
    if (preferredInputMode.value) {
      return preferredInputMode.value;
    }
    
    // Si hay contenido en la posición 2, determinamos el modo basándose en eso
    if (inputs.value[2] && /^\d$/.test(inputs.value[2])) {
      return 'numeric';
    }
    
    // Por defecto, empezamos con texto para posiciones 2-3
    return 'text';
  }
  
  return 'text';
};

const handleFocus = (index) => {
  if (index === activeIndex.value) {
    inputs.value[index] = "";
    emit("changePlate", inputs.value.join(""));
  }
};

const handleCLick = (index) => {
  if (index !== activeIndex.value) {
    nextTick(() => {
      inputRefs.value[activeIndex.value]?.focus();
    });
  }
};

const handleInput = (event, index) => {
  const allowedPattern = /^[a-zA-Z0-9]$/;
  const value = event.target.value.toUpperCase();

  if (allowedPattern.test(value)) {
    inputs.value[index] = value;
    
    // Lógica especial para posiciones 2 y 3
    if (index === 2) {
      // Establecer el modo preferido basándose en lo que escribió en posición 2
      if (/^\d$/.test(value)) {
        preferredInputMode.value = 'numeric';
      } else {
        preferredInputMode.value = 'text';
      }
    }
    
    // Emitir el cambio inmediatamente
    emit("changePlate", inputs.value.join(""));
    
    if (index < inputs.value.length - 1) {
      activeIndex.value = index + 1;
      nextTick(() => inputRefs.value[activeIndex.value]?.focus());
    } else {
      const isComplete = inputs.value.every(input => input.trim() !== "");
      if (isComplete) {
        emit("plateCompleted");
      }
    }
  } else {
    inputs.value[index] = "";
    emit("changePlate", inputs.value.join(""));
  }
};

const handleKeyup = async (event, index) => {
  await nextTick();

  if (event.key === "Backspace" || event.key === "Delete") {
    inputs.value[index] = "";
    emit("changePlate", inputs.value.join(""));

    // Si estamos borrando la posición 2, resetear el modo preferido
    if (index === 2) {
      preferredInputMode.value = null;
    }

    if (index > 0) {
      activeIndex.value = index - 1;
      nextTick(() => inputRefs.value[activeIndex.value]?.focus());
    }
  }
};

const clearPlate = () => {
  inputs.value = ["", "", "", "", "", ""];
  activeIndex.value = 0;
  preferredInputMode.value = null; // Resetear el modo preferido
  nextTick(() => inputRefs.value[0]?.focus());
};

onMounted(() => {
  nextTick(() => {
    inputRefs.value[0]?.focus();
  });
});

watch(() => props.value, (newValue) => {
  if (newValue && newValue.length === 6) {
    inputs.value = newValue.split('');
    activeIndex.value = 5; // Posición final
    //console.log('InputCarPlate sincronizado con valor del store:', newValue);
  } else if (!newValue) {
    inputs.value = ["", "", "", "", "", ""];
    activeIndex.value = 0;
  }
}, { immediate: true });

defineExpose({
  clearPlate
});
</script>

<style scoped>
  .license-plate {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 1rem 0rem 0rem 0rem;
  }

  .plate-frame {
    position: relative;
    width: 100%;
    height: auto;
    background-color: white;
    border: 2px solid;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 1rem 0.6rem 0rem 0.6rem;
    transition: border-color 0.3s ease;
  }

  /* Border verde cuando la patente está incompleta */
  .plate-incomplete {
    border-color: #CDCDCD ;
  }

  /* Border negro cuando la patente está completa */
  .plate-complete {
    border-color: black;
  }

  .inputs-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  /* Wrapper para posicionar la línea dentro del input */
  .input-wrapper {
    width: 10%;
    position: relative;
    display: flex;
    justify-content: center;
  }

  .input-box {
    background-color: #f6f6f6;
    border: 1px solid #ddd;
    outline: none;
    position: relative;
  }

  .input-box:focus {
    border: 2px solid #238b00;
  }

  /* Línea flotante dentro del input */
  .input-line {
    position: absolute;
    bottom: 8px;
    width: 80%;
    height: 2px;
    background-color: #333;
    border-radius: 1px;
    pointer-events: none;
  }

  .input-wrapper:focus-within .input-line {
    background-color: #238b00;
  }

  .dot-separator {
    font-size: 2.5rem;
    font-weight: bold;
    display: flex;
    align-items: center;
  }

  .plate-text {
    font-size: 0.8rem;
    font-weight: bold;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 8px;
  }
</style>